import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Plus, Upload, X, Trash2, CircleDot, Circle, Pencil, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { FURNITURE_CATEGORIES } from '@/lib/furnitureCatalog';
import { filterPieces } from '@/lib/catalogFilter';
import {
  listFurniturePieces,
  createFurniturePiece,
  updateFurniturePiece,
  deleteFurniturePiece,
  toggleFurniturePieceActive,
  importPiecePreview,
  importPieceCommit,
  type FurniturePieceWithUrl,
  type CatalogImportPreview,
} from '@/services/furnitureCatalogService';

const SELECT_CLASS =
  'w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring';

// Freitext-Kategorie mit Vorschlagsliste (datalist). Admin kann wählen oder tippen.
function CategoryInput({
  value,
  onChange,
  suggestions,
  listId,
}: {
  value: string;
  onChange: (v: string) => void;
  suggestions: string[];
  listId: string;
}) {
  return (
    <>
      <input
        type="text"
        list={listId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="z.B. couchtisch_klein"
        className={SELECT_CLASS}
      />
      <datalist id={listId}>
        {suggestions.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
    </>
  );
}

function AddPieceDialog({
  suggestions,
  onClose,
  onCreated,
}: {
  suggestions: string[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [name, setName] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setPicked = (f: File) => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const clearPicked = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!file) {
      setError('Bitte ein Freisteller-Bild wählen.');
      return;
    }
    if (!category.trim()) {
      setError('Bitte eine Kategorie angeben.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await createFurniturePiece({ file, category: category.trim(), name });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Neues Möbelstück</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {preview ? (
            <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden border border-border bg-muted/30">
              <img src={preview} alt="Vorschau" className="w-full h-full object-contain" />
              <button
                type="button"
                onClick={clearPicked}
                className="absolute top-2 right-2 p-1 rounded-full bg-background/80 hover:bg-background"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div
              className="w-full aspect-[4/3] rounded-lg border-2 border-dashed border-border bg-muted/30 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files[0];
                if (f && f.type.startsWith('image/')) setPicked(f);
              }}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="w-6 h-6 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Freisteller hochladen</span>
              <span className="text-xs text-muted-foreground/60">oder hierher ziehen</span>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) setPicked(f);
            }}
          />

          <div className="space-y-2">
            <Label>Name</Label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Skandi 3-Sitzer hellgrau"
              className={SELECT_CLASS}
            />
          </div>
          <div className="space-y-2">
            <Label>Kategorie</Label>
            <CategoryInput value={category} onChange={setCategory} suggestions={suggestions} listId="cat-add" />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Speichern...
              </>
            ) : (
              'Speichern'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditPieceDialog({
  piece,
  suggestions,
  onClose,
  onSaved,
}: {
  piece: FurniturePieceWithUrl;
  suggestions: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [name, setName] = useState<string>(piece.name ?? '');
  const [category, setCategory] = useState<string>(piece.category ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setPicked = (f: File) => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const clearNewImage = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!category.trim()) {
      setError('Bitte eine Kategorie angeben.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await updateFurniturePiece(piece.id, { name, category: category.trim(), file });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const shownImage = preview ?? piece.url;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Möbelstück bearbeiten</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden border border-border bg-muted/30">
            <img src={shownImage} alt="Vorschau" className="w-full h-full object-contain" />
            {preview ? (
              <button
                type="button"
                onClick={clearNewImage}
                className="absolute top-2 right-2 p-1 rounded-full bg-background/80 hover:bg-background"
                title="Neues Bild verwerfen (Original behalten)"
              >
                <X className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-md bg-background/80 hover:bg-background text-xs"
              >
                <Upload className="w-3.5 h-3.5" /> Bild ersetzen
              </button>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) setPicked(f);
            }}
          />

          <div className="space-y-2">
            <Label>Name</Label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Skandi 3-Sitzer hellgrau"
              className={SELECT_CLASS}
            />
          </div>
          <div className="space-y-2">
            <Label>Kategorie</Label>
            <CategoryInput value={category} onChange={setCategory} suggestions={suggestions} listId="cat-edit" />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Speichern...
              </>
            ) : (
              'Speichern'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportPieceDialog({
  suggestions,
  onClose,
  onImported,
}: {
  suggestions: string[];
  onClose: () => void;
  onImported: () => void;
}) {
  const [url, setUrl] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [preview, setPreview] = useState<CatalogImportPreview | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLoad = async () => {
    if (!url.trim()) {
      setError('Bitte eine Produkt-URL einfügen.');
      return;
    }
    setIsLoadingPreview(true);
    setError(null);
    try {
      const result = await importPiecePreview(url.trim());
      setPreview(result);
      setName(result.name);
      setCategory(result.category);
      setSelectedImage(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Seite konnte nicht geladen werden');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleSave = async () => {
    if (!selectedImage) {
      setError('Bitte das freigestellte Bild auswählen.');
      return;
    }
    if (!category.trim()) {
      setError('Bitte eine Kategorie angeben.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await importPieceCommit({ imageUrl: selectedImage, name: name.trim(), category: category.trim() });
      onImported();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl flex flex-col max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>Aus Shop importieren</DialogTitle>
        </DialogHeader>

        {/* Innerer Scroll-Container (min-h-0 statt overflow auf DialogContent). */}
        <div className="min-h-0 overflow-y-auto space-y-4 py-2 pr-1">
          <div className="space-y-2">
            <Label>Produkt-URL</Label>
            <div className="flex gap-2">
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.sklum.com/de/..."
                className={SELECT_CLASS}
                onKeyDown={(e) => e.key === 'Enter' && !isLoadingPreview && handleLoad()}
              />
              <Button variant="outline" onClick={handleLoad} disabled={isLoadingPreview}>
                {isLoadingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Laden'}
              </Button>
            </div>
          </div>

          {preview && (
            <>
              <div className="space-y-2">
                <Label>Freigestelltes Bild wählen</Label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {preview.images.map((img) => (
                    <button
                      key={img}
                      type="button"
                      onClick={() => setSelectedImage(img)}
                      className={`relative aspect-square rounded-md overflow-hidden border-2 bg-muted/30 transition-colors ${
                        selectedImage === img ? 'border-primary ring-2 ring-primary' : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-contain" />
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Name</Label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="z.B. Esszimmerstuhl Kaela"
                  className={SELECT_CLASS}
                />
              </div>
              <div className="space-y-2">
                <Label>Kategorie</Label>
                <CategoryInput value={category} onChange={setCategory} suggestions={suggestions} listId="cat-import" />
              </div>
            </>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !preview || !selectedImage}>
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Speichern...
              </>
            ) : (
              'Speichern'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AdminFurnitureCatalogView() {
  const [pieces, setPieces] = useState<FurniturePieceWithUrl[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editingPiece, setEditingPiece] = useState<FurniturePieceWithUrl | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [activeOnly, setActiveOnly] = useState(false);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      setPieces(await listFurniturePieces());
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Vorschläge = vordefinierte Kategorien + bereits genutzte, alphabetisch.
  const categorySuggestions = useMemo(() => {
    const set = new Set<string>([...FURNITURE_CATEGORIES, ...pieces.map((p) => p.category)]);
    return Array.from(set).filter(Boolean).sort();
  }, [pieces]);

  const visiblePieces = filterPieces(pieces, { query, category: categoryFilter, activeOnly });

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteFurniturePiece(id);
      setPieces((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Fehler beim Löschen');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggle = async (piece: FurniturePieceWithUrl) => {
    const next = !piece.is_active;
    setPieces((prev) => prev.map((p) => (p.id === piece.id ? { ...p, is_active: next } : p)));
    try {
      await toggleFurniturePieceActive(piece.id, next);
    } catch {
      // Rollback bei Fehler
      setPieces((prev) => prev.map((p) => (p.id === piece.id ? { ...p, is_active: !next } : p)));
    }
  };

  return (
    <div className="space-y-4">
      {showAdd && (
        <AddPieceDialog
          suggestions={categorySuggestions}
          onClose={() => setShowAdd(false)}
          onCreated={loadData}
        />
      )}
      {showImport && (
        <ImportPieceDialog
          suggestions={categorySuggestions}
          onClose={() => setShowImport(false)}
          onImported={loadData}
        />
      )}
      {editingPiece && (
        <EditPieceDialog
          piece={editingPiece}
          suggestions={categorySuggestions}
          onClose={() => setEditingPiece(null)}
          onSaved={loadData}
        />
      )}

      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">Möbel-Bibliothek</h2>
          <p className="text-sm text-muted-foreground">
            Freisteller-Bibliothek. Aktive Stücke speisen das katalog-gestützte Staging.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowImport(true)}>
            <Link2 className="w-4 h-4 mr-2" />
            Aus Shop importieren
          </Button>
          <Button onClick={() => setShowAdd(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Neues Möbelstück
          </Button>
        </div>
      </div>

      {loadError && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3 text-sm text-destructive">
          {loadError}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      ) : pieces.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          Noch keine Möbelstücke. Lege das erste an.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name suchen…"
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            />
            <select
              value={categoryFilter ?? ''}
              onChange={(e) => setCategoryFilter(e.target.value || null)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">Alle Kategorien</option>
              {FURNITURE_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <label className="flex items-center gap-1 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
              Nur aktive
            </label>
            <span className="text-xs text-muted-foreground">{visiblePieces.length} Stück</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {visiblePieces.map((piece) => (
            <div
              key={piece.id}
              className={`rounded-lg border overflow-hidden ${
                piece.is_active ? 'border-border' : 'border-border opacity-50'
              }`}
            >
              <div className="aspect-[4/3] bg-muted/30 flex items-center justify-center">
                <img src={piece.url} alt={piece.category} className="w-full h-full object-contain" />
              </div>
              <div className="p-2 space-y-1">
                <p className="text-xs font-medium truncate">{piece.name || piece.category}</p>
                <p className="text-[11px] text-muted-foreground truncate">{piece.category}</p>
                <div className="flex items-center justify-between pt-1">
                  <button
                    type="button"
                    onClick={() => handleToggle(piece)}
                    className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                  >
                    {piece.is_active ? (
                      <CircleDot className="w-3.5 h-3.5 text-green-600" />
                    ) : (
                      <Circle className="w-3.5 h-3.5" />
                    )}
                    {piece.is_active ? 'Aktiv' : 'Inaktiv'}
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setEditingPiece(piece)}
                      className="text-muted-foreground hover:text-foreground"
                      title="Bearbeiten"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(piece.id)}
                      disabled={deletingId === piece.id}
                      className="text-muted-foreground hover:text-destructive"
                      title="Löschen"
                    >
                      {deletingId === piece.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          </div>
        </>
      )}
    </div>
  );
}
